import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import isoLanguages from '../../isoLanguages';
import '../../DashboardHome/settings.css';
import './settings.css';

const ICN = {
    globe: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 8h10M8 3c1.5 1.5 1.5 8 0 10M8 3c-1.5 1.5-1.5 8 0 10" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    ),
    general: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 1.5v2M8 12.5v2M14.5 8h-2M3.5 8h-2M12.6 3.4l-1.4 1.4M4.8 11.2l-1.4 1.4M12.6 12.6l-1.4-1.4M4.8 4.8L3.4 3.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
};

const SECTIONS = [
    { id: 'localization', label: 'Localization', tail: 'Defaults', group: 'Workspace', icon: ICN.globe },
    { id: 'region', label: 'Region & formats', group: 'Workspace', icon: ICN.general },
];

const QA_CHECKS = [
    ['tags', 'Tags & placeholders', 'Flag missing, extra, or reordered inline tags.'],
    ['numbers', 'Number consistency', 'Catch numerals that differ between source and target.'],
    ['spaces', 'Double spaces', 'Detect repeated or trailing whitespace.'],
    ['punct', 'Ending punctuation', 'Warn when terminal punctuation doesn’t match the source.'],
    ['untranslated', 'Untranslated segments', 'Flag targets identical to the source language.'],
    ['terminology', 'Terminology', 'Enforce approved glossary terms during QA.'],
    ['length', 'Length limits', 'Warn when a target exceeds its character budget.'],
    ['formality', 'Formality / register', 'Flag du/Sie, tu/vous mismatches against the project tone.'],
];

const DATE_FORMATS = [
    { value: 'dmy', label: '31/05/26' },
    { value: 'mdy', label: '05/31/26' },
    { value: 'iso', label: '2026-05-31' },
];

const REGIONS = [
    { value: 'eu', label: 'Europe (Frankfurt)' },
    { value: 'us', label: 'North America (Virginia)' },
    { value: 'apac', label: 'Asia Pacific (Singapore)' },
];

const TIMEZONES = [
    { value: 'cet', label: '(GMT+1) Central European' },
    { value: 'gmt', label: '(GMT+0) London' },
    { value: 'est', label: '(GMT−5) Eastern' },
    { value: 'pst', label: '(GMT−8) Pacific' },
    { value: 'jst', label: '(GMT+9) Tokyo' },
];

const MT_ENGINES = [
    { value: 'rush', label: 'Rush AI (adaptive)' },
    { value: 'deepl', label: 'DeepL Pro' },
    { value: 'google', label: 'Google Cloud' },
    { value: 'none', label: 'No engine' },
];

// ============ Primitives ============
const SetBlock = ({ title, desc, tag, children }) => (
    <div className="set-block">
        <div className="set-block-head">
            <h3>{title}{tag && <span className="tag">{tag}</span>}</h3>
            {desc && <p>{desc}</p>}
        </div>
        {children}
    </div>
);

const SetField = ({ label, desc, stack, top, children }) => (
    <div className={`set-field ${stack ? 'stack' : ''} ${top ? 'top' : ''}`}>
        <div className="set-field-label">
            <div className="lbl">{label}</div>
            {desc && <div className="desc">{desc}</div>}
        </div>
        <div className="set-field-control">{children}</div>
    </div>
);

const SetSelect = ({ value, onChange, options, minWidth }) => (
    <select
        className="set-input"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={minWidth ? { minWidth } : undefined}
    >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

const SetSegmented = ({ value, onChange, options }) => (
    <div className="bv-seg">
        {options.map((o) => (
            <button
                type="button"
                key={o.value}
                className={value === o.value ? 'on' : ''}
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const SetToggle = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(!checked)}
        aria-pressed={checked}
        style={{
            width: 38, height: 22, borderRadius: 999,
            border: 0,
            background: checked ? 'var(--accent)' : 'var(--bg-soft)',
            position: 'relative',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.4 : 1,
        }}
    >
        <span style={{
            position: 'absolute', top: 2,
            left: checked ? 18 : 2,
            width: 18, height: 18,
            background: 'white', borderRadius: 999,
            transition: 'left 0.15s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }} />
    </button>
);

const SetSlider = ({ value, min = 0, max = 100, step = 1, onChange, format }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 220 }}>
        <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            style={{ flex: 1 }}
            className="set-slider"
        />
        <span style={{ fontWeight: 700, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums', minWidth: 44, textAlign: 'right' }}>
            {format ? format(value) : value}
        </span>
    </div>
);

const SetRadioCards = ({ value, onChange, options }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
        {options.map((o) => (
            <button
                type="button"
                key={o.value}
                onClick={() => onChange(o.value)}
                style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    border: `1px solid ${value === o.value ? 'var(--accent)' : 'var(--line)'}`,
                    borderRadius: 10,
                    background: value === o.value ? 'var(--accent-soft)' : 'var(--bg)',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    color: 'inherit',
                    display: 'flex', flexDirection: 'column', gap: 3,
                }}
            >
                <b style={{ fontSize: 13.5, color: 'var(--ink)' }}>{o.label}</b>
                <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{o.desc}</span>
            </button>
        ))}
    </div>
);

const SetLangChips = ({ langs, all, onAdd, onRemove }) => {
    const [open, setOpen] = useState(false);
    const codes = new Set(langs.map((l) => l.code));
    const available = all.filter((l) => !codes.has(l.code));
    return (
        <div className="np-people" style={{ position: 'relative' }}>
            {langs.map((l) => (
                <span key={l.code} className="np-person-chip">
                    <span className="av-s av-1" style={{ fontSize: 9 }}>{l.code.slice(0, 2)}</span>
                    <span className="nm">{l.name}</span>
                    <button type="button" className="x" onClick={() => onRemove(l.code)}>
                        <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
                            <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                        </svg>
                    </button>
                </span>
            ))}
            <button type="button" className="np-person-add" onClick={() => setOpen((o) => !o)}>
                + Add language
            </button>
            {open && (
                <div className="np-person-pop" style={{ maxHeight: 280, overflow: 'auto' }}>
                    {available.length === 0 && <div className="empty">All languages added</div>}
                    {available.slice(0, 50).map((l) => (
                        <div key={l.code} className="opt" role="button" tabIndex={0} onClick={() => { onAdd(l); setOpen(false); }}>
                            <span className="av-s av-1" style={{ fontSize: 9 }}>{l.code.slice(0, 2)}</span>
                            <div className="meta"><b>{l.name}</b><span>{l.code}</span></div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ============ Sections ============
const LocalizationSection = ({ s, set, sourceLangs, langCatalog }) => {
    const loc = s.loc;
    return (
        <>
            <div className="set-section-head">
                <h2>Localization defaults</h2>
                <p>How translation memory, glossaries, machine translation, and quality checks behave by default. Individual projects can override any of these.</p>
            </div>

            <SetBlock title="Languages">
                <SetField label="Default source language" desc="The language new content is authored in.">
                    <SetSelect
                        value={loc.sourceLang}
                        onChange={(v) => set('loc.sourceLang', v)}
                        options={sourceLangs}
                        minWidth={220}
                    />
                </SetField>
                <SetField label="Target languages" desc={`${loc.targets.length} languages added to new projects automatically.`} stack top>
                    <SetLangChips
                        langs={loc.targets}
                        all={langCatalog}
                        onAdd={(l) => set('loc.targets', [...loc.targets, l])}
                        onRemove={(code) => set('loc.targets', loc.targets.filter((t) => t.code !== code))}
                    />
                </SetField>
            </SetBlock>

            <SetBlock title="Translation memory" tag="TM" desc="Reuse previously approved translations to keep wording consistent and cut cost.">
                <SetField label="Auto-apply matches" desc="Pre-fill segments with the best TM match when a file opens.">
                    <SetToggle checked={loc.tmAutoApply} onChange={(v) => set('loc.tmAutoApply', v)} />
                </SetField>
                <SetField label="Minimum match to auto-apply" desc="Only matches at or above this score are inserted automatically.">
                    <SetSlider value={loc.tmThreshold} min={50} max={100} step={5} onChange={(v) => set('loc.tmThreshold', v)} format={(v) => `${v}%`} />
                </SetField>
                <SetField label="Auto-propagate 100% matches" desc="Repeated identical segments fill in across the whole file.">
                    <SetToggle checked={loc.tmPropagate} onChange={(v) => set('loc.tmPropagate', v)} />
                </SetField>
                <SetField label="Cross-project penalty" desc="Lower the score of matches that come from a different project.">
                    <SetSlider value={loc.tmPenalty} min={0} max={20} step={1} onChange={(v) => set('loc.tmPenalty', v)} format={(v) => `−${v}%`} />
                </SetField>
                <SetField label="Context matching (ICE)" desc="Treat a match as 101% when the surrounding segments also match.">
                    <SetToggle checked={loc.tmContextMatch} onChange={(v) => set('loc.tmContextMatch', v)} />
                </SetField>
                <SetField label="Write confirmed segments back to TM" desc="Approved translations are saved to the memory for reuse.">
                    <SetToggle checked={loc.tmWriteBack} onChange={(v) => set('loc.tmWriteBack', v)} />
                </SetField>
            </SetBlock>

            <SetBlock title="Glossary & terminology" desc="Control how approved and forbidden terms are enforced in the editor.">
                <div className="set-field stack top">
                    <div className="set-field-label">
                        <div className="lbl">Enforcement level</div>
                        <div className="desc">What happens when a target ignores an approved glossary term.</div>
                    </div>
                    <div className="set-field-control" style={{ width: '100%' }}>
                        <SetRadioCards
                            value={loc.glossaryMode}
                            onChange={(v) => set('loc.glossaryMode', v)}
                            options={[
                                { value: 'off', label: 'Off', desc: 'No glossary checks while translating.' },
                                { value: 'warn', label: 'Warn', desc: 'Show a non-blocking warning on the segment.' },
                                { value: 'block', label: 'Block', desc: 'Segment can’t be confirmed until resolved.' },
                            ]}
                        />
                    </div>
                </div>
                <SetField label="Case-sensitive matching" desc="Treat “Workspace” and “workspace” as different terms.">
                    <SetToggle checked={loc.glossaryCase} onChange={(v) => set('loc.glossaryCase', v)} />
                </SetField>
                <SetField label="Highlight forbidden terms" desc="Mark do-not-use terms in red as they’re typed.">
                    <SetToggle checked={loc.glossaryForbidden} onChange={(v) => set('loc.glossaryForbidden', v)} />
                </SetField>
            </SetBlock>

            <SetBlock title="Machine translation" tag="MT" desc="Seed empty segments with machine output, then post-edit. TM and glossary always take priority.">
                <SetField label="Default engine">
                    <SetSelect value={loc.mtEngine} onChange={(v) => set('loc.mtEngine', v)} options={MT_ENGINES} minWidth={180} />
                </SetField>
                <SetField label="Pre-translate new files" desc="Run MT on import so linguists start from a draft.">
                    <SetToggle checked={loc.mtPretranslate} onChange={(v) => set('loc.mtPretranslate', v)} disabled={loc.mtEngine === 'none'} />
                </SetField>
                <SetField label="Apply only above confidence" desc="Discard low-confidence MT and leave those segments empty.">
                    <SetSlider value={loc.mtThreshold} min={0} max={100} step={5} onChange={(v) => set('loc.mtThreshold', v)} format={(v) => `${v}%`} />
                </SetField>
            </SetBlock>

            <SetBlock title="Quality assurance" desc="Automated checks run before a file can be marked complete.">
                {QA_CHECKS.map(([key, label, desc]) => (
                    <SetField key={key} label={label} desc={desc}>
                        <SetToggle checked={loc.qa[key]} onChange={(v) => set(`loc.qa.${key}`, v)} />
                    </SetField>
                ))}
            </SetBlock>
        </>
    );
};

const RegionFormatsSection = ({ s, set }) => (
    <>
        <div className="set-section-head">
            <h2>Region &amp; formats</h2>
            <p>Defaults for new projects and how dates and numbers render in the editor.</p>
        </div>

        <SetBlock title="Locale defaults" desc="Where this workspace runs and how times and dates are presented to linguists.">
            <SetField label="Primary region" desc="Determines data residency and the default server for new projects.">
                <SetSelect value={s.general.region} onChange={(v) => set('general.region', v)} options={REGIONS} />
            </SetField>
            <SetField label="Time zone" desc="Used for deadlines, digests, and activity timestamps.">
                <SetSelect value={s.general.timezone} onChange={(v) => set('general.timezone', v)} options={TIMEZONES} minWidth={200} />
            </SetField>
            <SetField label="Date format">
                <SetSegmented value={s.general.dateFormat} onChange={(v) => set('general.dateFormat', v)} options={DATE_FORMATS} />
            </SetField>
            <SetField label="First day of week">
                <SetSegmented
                    value={s.general.weekStart}
                    onChange={(v) => set('general.weekStart', v)}
                    options={[
                        { value: 'mon', label: 'Monday' },
                        { value: 'sun', label: 'Sunday' },
                    ]}
                />
            </SetField>
        </SetBlock>
    </>
);

// ============ Main ============
const deepSet = (obj, path, value) => {
    const parts = path.split('.');
    const next = { ...obj };
    let cur = next;
    for (let i = 0; i < parts.length - 1; i += 1) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = value;
    return next;
};

const Settings = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [active, setActive] = useState('localization');
    const [state, setState] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => {
                setState(r.data.workspaceSettings || {
                    general: { region: 'eu', timezone: 'cet', dateFormat: 'iso', weekStart: 'mon' },
                    loc: {
                        sourceLang: 'en',
                        targets: [{ code: 'DE', name: 'German' }, { code: 'FR', name: 'French' }],
                        tmAutoApply: true,
                        tmThreshold: 75,
                        tmPropagate: true,
                        tmPenalty: 5,
                        tmContextMatch: true,
                        tmWriteBack: true,
                        glossaryMode: 'warn',
                        glossaryCase: false,
                        glossaryForbidden: true,
                        mtEngine: 'rush',
                        mtPretranslate: true,
                        mtThreshold: 60,
                        qa: { tags: true, numbers: true, spaces: true, punct: true, untranslated: true, terminology: true, length: false, formality: true },
                    },
                });
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load settings.');
                    setLoading(false);
                }
            });
    }, [token, dispatch, navigate]);

    const set = (path, value) => {
        setState((prev) => deepSet(prev, path, value));
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            await axios.put('/api/users/me', { workspaceSettings: state }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDirty(false);
        } catch (e) {
            setError('Save failed.');
        } finally {
            setSaving(false);
        }
    };

    const sourceLangs = useMemo(() => isoLanguages.map((l) => ({ value: l.code, label: l.name })), []);
    const langCatalog = useMemo(() => isoLanguages.map((l) => ({ code: l.code.toUpperCase(), name: l.name })), []);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading settings…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;
    if (!state) return null;

    const currentSection = SECTIONS.find((x) => x.id === active);

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Workspace settings</h1>
            </div>
            <p className="rs-page-sub">Localization defaults that apply to every new project. Individual projects can override these.</p>

            <div className="set-layout">
                <aside className="set-rail">
                    <div className="set-rail-group">
                        <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 12px 6px' }}>
                            Workspace
                        </div>
                        {SECTIONS.map((sec) => (
                            <button
                                key={sec.id}
                                type="button"
                                className={`set-rail-item ${active === sec.id ? 'on' : ''}`}
                                onClick={() => setActive(sec.id)}
                            >
                                <span className="ic">{sec.icon}</span>
                                <span>{sec.label}</span>
                                {sec.tail && <span className="tail">{sec.tail}</span>}
                            </button>
                        ))}
                    </div>
                </aside>

                <div className="set-body">
                    {active === 'localization' && (
                        <LocalizationSection s={state} set={set} sourceLangs={sourceLangs} langCatalog={langCatalog} />
                    )}
                    {active === 'region' && (
                        <RegionFormatsSection s={state} set={set} />
                    )}
                </div>
            </div>

            {dirty && (
                <div style={{
                    position: 'sticky', bottom: 16, marginTop: 16,
                    background: 'var(--bg-card)', border: '1px solid var(--line)',
                    borderRadius: 12, padding: 12, display: 'flex',
                    alignItems: 'center', gap: 12, boxShadow: 'var(--shadow-md)',
                }}>
                    <span style={{ flex: 1, fontSize: 13, color: 'var(--ink-3)' }}>
                        You have unsaved changes.
                    </span>
                    <button type="button" className="btn-q" onClick={() => window.location.reload()}>Discard</button>
                    <button type="button" className="btn-acc" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save changes'}
                    </button>
                </div>
            )}
        </>
    );
};

export default Settings;
